import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { usePermissions } from '../context/PermissionsContext';
import { apiClient } from '../api';
import { formatDateTime, topAndTail } from '../utils/stringUtils';
import { DeleteButton } from './shared/DeleteButton';

interface Gateway {
  id: string;
  name: string;
  description: string;
  did: string;
  gateway_type: 'self' | 'remote';
  status: 'active' | 'disabled' | 'pending' | 'failed' | 'awaiting-approval';
  created_at: string;
  updated_at: string;
}

interface ConnectionPoint {
  id: string;
  gateway_id: string;
  mediator_id: string;
  name: string;
  description: string;
  oob_url: string;
  use_count: number;
  created_at: string;
  last_used_at?: string;
  expires_at?: string;
  cp_type?: 'user' | 'system'; // Optional for backward compatibility
  secret?: string; // Optional secret for OOB connection validation
  exposed_channels?: string[]; // List of channel config IDs to expose
  enabled?: boolean; // Whether the connection point is enabled
}

interface Mediator {
  id: string;
  name: string;
  description: string;
  did: string;
  status: string;
  created_at: string;
  updated_at: string;
  did_document?: any;
}

const GatewaysPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [gateways, setGateways] = useState<Gateway[]>([]);
  const [connectionPoints, setConnectionPoints] = useState<ConnectionPoint[]>([]);
  const [mediators, setMediators] = useState<Mediator[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPubs, setLoadingPubs] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasSelfGateway, setHasSelfGateway] = useState(false);
  const [pingStatus, setPingStatus] = useState<{ [key: string]: 'pinging' | 'success' | 'failed' }>({});
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchGateways();
    fetchConnectionPoints();
    fetchMediators();
  }, []);

  const fetchGateways = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/gateways');
      setGateways(response.data);
      // Check if there's a self gateway
      setHasSelfGateway(response.data.some((g: Gateway) => g.gateway_type === 'self'));
    } catch (error: any) {
      setError(error.message || 'Failed to load gateways');
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectionPoints = async () => {
    try {
      setLoadingPubs(true);
      const response = await apiClient.get('/connection-points');
      setConnectionPoints(response.data);
    } catch (error: any) {
      console.error('Failed to load connection points:', error);
      // Don't show error for connection points - it's optional
    } finally {
      setLoadingPubs(false);
    }
  };

  const fetchMediators = async () => {
    try {
      const response = await apiClient.get('/mediators');
      setMediators(response.data);
    } catch (error: any) {
      console.error('Failed to load mediators:', error);
      // Don't show error for mediators - it's optional
    }
  };

  const getMediatorById = (mediatorId: string): Mediator | undefined => {
    return mediators.find(m => m.id === mediatorId);
  };

  // Filter gateways based on search term
  const { getCurrentStats } = useApp();
  const stats = getCurrentStats();
  const filteredGateways = React.useMemo(() => {
    const trimmedSearch = searchTerm.trim();
    if (!trimmedSearch) return gateways;
    
    const searchLower = trimmedSearch.toLowerCase();
    const channels = stats?.channels || [];
    
    return gateways.filter(gateway => {
      // Search by name, description, ID, DID
      if (
        gateway.name?.toLowerCase().includes(searchLower) ||
        gateway.description?.toLowerCase().includes(searchLower) ||
        gateway.id?.toLowerCase().includes(searchLower) ||
        gateway.did?.toLowerCase().includes(searchLower)
      ) {
        return true;
      }
      
      // Check if any connection point for this gateway matches
      const hasMatchingConnectionPoint = connectionPoints.some(cp => 
        cp.gateway_id === gateway.id && (
          cp.name?.toLowerCase().includes(searchLower) ||
          cp.description?.toLowerCase().includes(searchLower) ||
          cp.id?.toLowerCase().includes(searchLower) ||
          cp.oob_url?.toLowerCase().includes(searchLower)
        )
      );
      
      if (hasMatchingConnectionPoint) {
        return true;
      }
      
      // For remote gateways, also search by connected channels via fabric
      // Check if any channel targets this gateway via fabric://gateway_id/...
      const hasConnectedChannel = channels.some(channel => {
        if (channel.target_endpoint?.startsWith('fabric://')) {
          const fabricPath = channel.target_endpoint.substring(9); // Remove 'fabric://'
          const [targetGatewayId] = fabricPath.split('/');
          return targetGatewayId === gateway.id;
        }
        return false;
      });
      
      if (hasConnectedChannel) {
        // Check if gateway ID matches search (partial match)
        return gateway.id.toLowerCase().includes(searchLower);
      }
      
      return false;
    });
  }, [gateways, searchTerm, stats?.channels, connectionPoints]);

  // Filter connection points based on search term
  const filteredConnectionPoints = React.useMemo(() => {
    const trimmedSearch = searchTerm.trim();
    if (!trimmedSearch) return connectionPoints;
    
    const searchLower = trimmedSearch.toLowerCase();
    return connectionPoints.filter(cp => 
      cp.name?.toLowerCase().includes(searchLower) ||
      cp.description?.toLowerCase().includes(searchLower) ||
      cp.id?.toLowerCase().includes(searchLower) ||
      cp.oob_url?.toLowerCase().includes(searchLower)
    );
  }, [connectionPoints, searchTerm]);

  const handleDeleteGateway = async (id: string) => {

    try {
      await apiClient.delete(`/gateways/${id}`);
      setSuccess('Gateway deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      fetchGateways();
    } catch (error: any) {
      setError(error.message || 'Failed to delete gateway');
    }
  };

  const handleDeleteConnectionPoint = async (id: string) => {

    try {
      await apiClient.delete(`/connection-points/${id}`);
      setSuccess('Connection point deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      fetchConnectionPoints();
    } catch (error: any) {
      setError(error.message || 'Failed to delete connection point');
    }
  };

  const handlePingGateway = async (id: string, name: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    setPingStatus(prev => ({ ...prev, [id]: 'pinging' }));
    
    try {
      const response = await apiClient.post(`/gateways/${id}/ping`);
      
      if (response.data.success) {
        setPingStatus(prev => ({ ...prev, [id]: 'success' }));
        setSuccess(`Ping to "${name}" successful (${response.data.round_trip_ms}ms)`);
        setTimeout(() => {
          setPingStatus(prev => {
            const newStatus = { ...prev };
            delete newStatus[id];
            return newStatus;
          });
          setSuccess(null);
        }, 3000);
      } else {
        setPingStatus(prev => ({ ...prev, [id]: 'failed' }));
        setError(`Ping to "${name}" failed: ${response.data.message}`);
        setTimeout(() => {
          setPingStatus(prev => {
            const newStatus = { ...prev };
            delete newStatus[id];
            return newStatus;
          });
        }, 3000);
      }
    } catch (error: any) {
      setPingStatus(prev => ({ ...prev, [id]: 'failed' }));
      setError(error.message || `Failed to ping gateway "${name}"`);
      setTimeout(() => {
        setPingStatus(prev => {
          const newStatus = { ...prev };
          delete newStatus[id];
          return newStatus;
        });
      }, 3000);
    }
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    //setSuccess('Connection point link copied to clipboard!');
    setTimeout(() => setSuccess(null), 2000);
  };

  const handleCopySecret = (secret: string) => {
    navigator.clipboard.writeText(secret);
    //setSuccess('Connection secret copied to clipboard!');
    setTimeout(() => setSuccess(null), 2000);
  };

  return (
    <div className="container-fluid">
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => navigate('/')}
          >
            <i className="fas fa-times"></i> Close
          </button>
          <div style={{ position: 'relative', display: 'inline-block', marginLeft: '0.75rem' }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: '0.875rem', pointerEvents: 'none' }}></i>
            <input
              type="text"
              className="form-control"
              placeholder="Filter Gateways..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '240px', paddingLeft: '2.25rem', backgroundColor: 'rgba(255, 255, 255, 0.3)', border: 'none', fontSize: '0.875rem', outline: 'none', boxShadow: 'none' }}
              onFocus={(e) => e.target.style.boxShadow = 'none'}
            />
          </div>
        </div>
        {hasPermission('gateways.edit') && (
          <div className="d-flex gap-2" style={{ gap: '0.5rem' }}>
            {hasSelfGateway && (
              <button 
                className="btn btn-sm btn-success" 
                onClick={() => navigate('/gateways/publish')}
              >
                <i className="fas fa-share-alt me-2"></i> Create Connection Point
              </button>
            )}
            <button 
              className="btn btn-sm btn-primary" 
              onClick={() => navigate('/gateways/connect')}
            >
              <i className="fas fa-plus me-2"></i> Connect to Trust Gateway
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="close" onClick={() => setError(null)} aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}
      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button type="button" className="close" onClick={() => setSuccess(null)} aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}

      {/* All Gateways Section */}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <div className="spinner-border" role="status" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>
            <span className="visually-hidden"></span>
          </div>
        </div>
      ) : gateways.length === 0 ? (
        <div className="card shadow mb-4">
          <div className="card-body text-center">
            <div className="text-muted">
              <i className="fas fa-server fa-3x mb-3"></i>
              <p>No gateways found.</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="card shadow mb-4">
          <div className="card-header py-3">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-server"></i> Trust Gateways
              <span className="badge badge-primary ml-2" style={{ verticalAlign: 'middle' }}>{filteredGateways.length}{searchTerm && ` of ${gateways.length}`}</span>
            </h6>
          </div>
          <div className="card-body">
            {filteredGateways.length === 0 ? (
              <div className="text-center">
                <div className="text-muted">
                  <i className="fas fa-search fa-3x mb-3"></i>
                  <p>No gateways match your search.</p>
                </div>
              </div>
            ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>DID</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGateways.map((gateway) => (
                    <tr 
                      key={gateway.id}
                      onClick={() => {
                        if (gateway.status !== 'pending' && gateway.status !== 'awaiting-approval') {
                          navigate(`/gateways/${gateway.id}`);
                        }
                      }}
                      style={{ cursor: gateway.status === 'pending' || gateway.status === 'awaiting-approval' ? 'default' : 'pointer' }}
                    >
                      <td>
                        {gateway.name}
                      </td>
                      <td>
                        <span className={`badge ${
                          gateway.gateway_type === 'self' ? 'badge-info' : 
                          gateway.gateway_type === 'remote' ? 'badge-success' : 
                          'badge-warning'
                        }`}>
                          {gateway.gateway_type === 'self' ? ' LOCAL ' : 
                           gateway.gateway_type === 'remote' ? ' REMOTE ' : 
                           'Unknown'}
                        </span>
                      </td>
                      <td><small>{gateway.description}</small></td>
                      <td><code style={{fontSize: '0.75rem'}}>{topAndTail(gateway.did,16,16)}</code></td>
                      <td >
                        <span className={`badge ${
                          gateway.status === 'active' ? 'badge-success' : 
                          gateway.status === 'failed' ? 'badge-danger' : 
                          gateway.status === 'awaiting-approval' ? 'badge-warning' : 
                          'badge-secondary'
                        }`}>
                          {gateway.status === 'awaiting-approval' ? 'AWAITING APPROVAL' : gateway.status.toUpperCase()}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'right' }}>
                        {hasPermission('gateways.edit') && (
                          <>
                            {gateway.status === 'awaiting-approval' && (
                              <button                                
                                title="Approve Gateway Connection"
                                className="btn btn-warning btn-sm me-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/gateways/${gateway.id}/approve`);
                                }}
                              >
                                <i className="fas fa-check"></i>
                              </button>
                            )}
                            {gateway.gateway_type !== 'self' && gateway.status !== 'awaiting-approval' && gateway.status !== 'pending' && (
                              <button
                                style={{marginLeft: '10px'}}
                                title="Ping Gateway - Test connectivity"
                                className={`btn btn-sm me-1 ${
                                  pingStatus[gateway.id] === 'pinging' ? 'btn-secondary' :
                                  pingStatus[gateway.id] === 'success' ? 'btn-success' :
                                  pingStatus[gateway.id] === 'failed' ? 'btn-danger' :
                                  'btn-info'
                                }`}
                                onClick={(e) => handlePingGateway(gateway.id, gateway.name, e)}
                                disabled={pingStatus[gateway.id] === 'pinging'}
                              >
                                {pingStatus[gateway.id] === 'pinging' ? (
                                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                ) : pingStatus[gateway.id] === 'success' ? (
                                  <i className="fas fa-check"></i>
                                ) : pingStatus[gateway.id] === 'failed' ? (
                                  <i className="fas fa-times"></i>
                                ) : (
                                  <i className="fas fa-heartbeat"></i>
                                )}
                              </button>
                            )}
                            {gateway.status !== 'awaiting-approval' && gateway.status !== 'pending' && (
                              <button
                                style={{marginLeft: '10px'}}
                                title="Edit Gateway"
                                className="btn btn-sm btn-primary me-1"
                                onClick={() => navigate(`/gateways/${gateway.id}`)}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                            )}
                          </>
                        )}
                        {hasPermission('gateways.delete') && gateway.gateway_type !== 'self' && (
                          <DeleteButton
                            onDelete={() => handleDeleteGateway(gateway.id)}
                            className="btn-sm"
                            title="Delete Gateway"
                            style={{marginLeft: '10px'}}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        </div>
      )}


      {/* Connection Points Section */}      {!loading && (      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <h6 className="m-0 font-weight-bold text-primary">
            <i className="fas fa-share-alt"></i> Connection Points
            <span className="badge badge-primary ml-2" style={{ verticalAlign: 'middle' }}>{filteredConnectionPoints.length}{searchTerm && ` of ${connectionPoints.length}`}</span>
          </h6>
        </div>
        <div className="card-body">
          {loadingPubs ? (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden"></span>
              </div>
            </div>
          ) : filteredConnectionPoints.length === 0 ? (
            <div className="text-center">
              <div className="text-muted">
                <i className="fas fa-search fa-3x mb-3"></i>
                <p>{searchTerm ? 'No connection points match your search.' : 'No connection points yet. Click "Create Connection Point" to expose a new connection point.'}</p>
              </div>
            </div>
          ) : (
              <div className="table-responsive">
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Mediator</th>
                      <th>Expires</th>
                      <th>Status</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredConnectionPoints.map((connectionPoint) => {
                      const mediator = getMediatorById(connectionPoint.mediator_id);
                      const isExpired = connectionPoint.expires_at ? new Date(connectionPoint.expires_at) < new Date() : false;
                      
                      return (
                        <tr 
                          key={connectionPoint.id}
                          onClick={() => navigate(`/connection-points/${connectionPoint.id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>{connectionPoint.name}</td>
                          <td><small>{connectionPoint.description}</small></td>
                          <td>
                            {mediator ? (
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  navigate('/mediators');
                                }}
                                className="text-primary"
                              >
                                {mediator.name}
                              </a>
                            ) : (
                              <span className="text-muted">Unknown</span>
                            )}
                          </td>
                          <td><small>{connectionPoint.expires_at ? formatDateTime(connectionPoint.expires_at, true) : '-'}</small></td>
                          <td>
                            {connectionPoint.enabled === false ? (
                              <span className="badge badge-warning">
                                <i className="fas fa-power-off"></i> DISABLED
                              </span>
                            ) : (
                              <span className={`badge badge-${isExpired ? 'danger' : 'success'}`}>
                                {isExpired ? 'EXPIRED' : 'VALID'}
                              </span>
                            )}
                          </td>
                          <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'right' }}>
                            {hasPermission('gateways.edit') && (
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => navigate(`/connection-points/${connectionPoint.id}`)}
                                title="Edit Connection Point"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-success"
                              style={{marginLeft: '10px'}}
                              onClick={() => handleCopyUrl(connectionPoint.oob_url)}
                              title="Copy Connection Point Link"
                            >
                              <i className="fas fa-link"></i>
                            </button>
                            {connectionPoint.secret && (
                              <button
                                className="btn btn-sm btn-secondary"
                                style={{marginLeft: '10px'}}
                                onClick={() => handleCopySecret(connectionPoint.secret!)}
                                title="Copy Connection Point Secret"
                              >
                                <i className="fas fa-key"></i>
                              </button>
                            )}
                            {hasPermission('gateways.delete') && (
                              <DeleteButton
                                onDelete={() => handleDeleteConnectionPoint(connectionPoint.id)}
                                className="btn-sm"
                                title="Delete connection point"
                                style={{marginLeft: '10px'}}
                              />
                            )}
                          </td>
                        </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GatewaysPage;
